import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Plus, Printer, Trash2, X, FileText, Pencil, Send, CheckCircle2, FileSignature, Smartphone, Mail, CreditCard } from "lucide-react";
import { toast } from "sonner";
import {
  fetchInvoices, createInvoice, updateInvoice, setInvoiceStatus, deleteInvoice,
  fetchClients, fetchFees, convertEstimate, formatApiError, sendInvoiceLink, getPayToken,
} from "@/lib/api";
import { COMPLEXITY_ITEMS, COMPLEXITY_CATEGORIES, US_STATES, UNSUPPORTED_STATES, NO_TAX_STATES } from "@/data/content";
import InvoiceSheet from "@/components/InvoiceSheet";
import { MobileInvoicePayment } from "./MobileInvoicePayment";

const inputCls =
  "min-w-0 w-full border border-steel-border bg-steel-elevated px-4 py-3 text-base sm:text-sm text-steel-text placeholder:text-steel-muted transition-colors duration-300 focus:border-steel-accent focus:outline-none rounded-none";

const STATUS_BADGE = {
  draft: "border-zinc-500/60 text-zinc-400",
  sent: "border-steel-brass/60 text-steel-brass",
  paid: "border-emerald-500/60 text-emerald-400",
  estimate: "border-amber-500/60 text-amber-400",
};

const BILLABLE_STATES = US_STATES.filter(
  (s) => !UNSUPPORTED_STATES.includes(s.code) && !NO_TAX_STATES.includes(s.code)
);

const labelForForm = (id) =>
  id === "base"
    ? "Base return preparation"
    : id === "multi-state"
      ? "State return preparation"
      : COMPLEXITY_ITEMS.find((i) => i.id === id)?.label || id;

const money = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const emptyBuilder = (kind) => ({
  id: null, kind, mode: kind === "mobile" ? "manual" : "client", client_user_id: "",
  recipient_name: "", recipient_email: "", lines: [], notes: "",
});

export default function InvoiceTab({ adminKey, initialKind = null }) {
  const [invoices, setInvoices] = useState(null);
  const [clients, setClients] = useState([]);
  const [fees, setFees] = useState(null);
  const [builder, setBuilder] = useState(() => initialKind ? emptyBuilder(initialKind) : null);
  const [view, setView] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formPick, setFormPick] = useState("");
  const [lineState, setLineState] = useState("");
  const detailRef = useRef(null);
  const builderRef = useRef(null);
  const returnOpened = useRef(Boolean(initialKind));
  const viewedId = view?.id;
  const builderOpen = Boolean(builder);
  const builderId = builder?.id;
  const ready = invoices !== null;

  useEffect(() => {
    if (viewedId && detailRef.current) {
      detailRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [viewedId]);

  useEffect(() => {
    if (ready && builderOpen && builderRef.current) {
      builderRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [builderOpen, builderId, ready]);

  const load = useCallback(async () => {
    try {
      const list = (await fetchInvoices(adminKey)).invoices;
      setInvoices(list);
      if (!returnOpened.current) {
        returnOpened.current = true;
        const id = new URLSearchParams(window.location.search).get("mobile_invoice");
        const returned = list.find((inv) => inv.id === id && inv.kind === "mobile");
        if (returned) setView(returned);
      }
    } catch {
      toast.error("Could not load invoices.");
    }
  }, [adminKey]);

  useEffect(() => {
    load();
    fetchClients(adminKey).then((d) => setClients(d.clients)).catch(() => {});
    fetchFees().then(setFees).catch(() => {});
     
  }, [adminKey, load]);

  const openNew = (mobile = false) => {
    setView(null);
    setFormPick("");
    setBuilder(emptyBuilder(mobile ? "mobile" : "standard"));
  };

  const openEdit = (inv) => {
    setView(null);
    setFormPick("");
    setBuilder({
      id: inv.id,
      kind: inv.kind || "standard",
      mode: inv.client_user_id ? "client" : "manual",
      client_user_id: inv.client_user_id || "",
      recipient_name: inv.recipient_name,
      recipient_email: inv.recipient_email,
      lines: inv.lines.map((l) => ({ ...l })),
      notes: inv.notes || "",
    });
  };

  const pickClient = (uid) => {
    const c = clients.find((x) => x.user_id === uid);
    setBuilder((b) => ({
      ...b,
      client_user_id: uid,
      recipient_name: c?.name || b.recipient_name,
      recipient_email: c?.email || b.recipient_email,
    }));
  };

  const addFormLine = () => {
    if (!formPick || !fees) return;
    if (formPick === "multi-state" && !lineState) return toast.error("Pick which state this return is for.");
    const price =
      formPick === "base"
        ? Number(fees.base_max) || 0
        : Number((fees.items || []).find((f) => f.id === formPick)?.max) || 0;
    const label =
      formPick === "multi-state" ? `State return preparation — ${lineState}` : labelForForm(formPick);
    setBuilder((b) => ({ ...b, lines: [...b.lines, { form_id: formPick, label, price }] }));
    setFormPick("");
    setLineState("");
  };

  const setLine = (i, field, value) =>
    setBuilder((b) => ({ ...b, lines: b.lines.map((l, idx) => (idx === i ? { ...l, [field]: value } : l)) }));

  const total = useMemo(
    () => (builder ? builder.lines.reduce((s, l) => s + (Number(l.price) || 0), 0) : 0),
    [builder]
  );

  const save = async (andSend) => {
    const p = {
      kind: builder.kind,
      client_user_id: builder.mode === "client" ? builder.client_user_id : "",
      recipient_name: builder.recipient_name.trim(),
      recipient_email: builder.recipient_email.trim(),
      notes: builder.notes.trim(),
      lines: builder.lines.map((l) => ({ label: String(l.label).trim(), price: Number(l.price) || 0, form_id: l.form_id || "" })),
    };
    const isManual = builder.kind === "mobile" || builder.mode === "manual";
    if (builder.mode === "client" && !p.client_user_id) return toast.error("Pick a portal client.");
    if (!p.recipient_name || !p.recipient_email) return toast.error("Add a recipient name and email.");
    if (p.lines.length === 0) return toast.error("Add at least one form or service line.");
    if (p.lines.some((l) => !l.label)) return toast.error("Every line needs a label.");
    if (p.lines.some((l) => !Number.isFinite(l.price) || l.price < 0)) return toast.error("Enter a valid price for every line.");
    if (isManual && (total < 0.50 || total > 999999.99)) return toast.error("Invoice total must be between $0.50 and $999,999.99 to email a pay link.");
    setSaving(true);
    try {
      let inv = builder.id
        ? await updateInvoice(adminKey, builder.id, p)
        : await createInvoice(adminKey, p);
      if (andSend) {
        if (isManual) {
          inv = await sendInvoiceLink(adminKey, inv.id);
          toast.success(`Invoice ${inv.number} emailed to ${inv.recipient_email} — they can sign and pay online.`);
        } else if (inv.status === "draft") {
          inv = await setInvoiceStatus(adminKey, inv.id, "sent");
          toast.success(`Invoice ${inv.number} sent — it's in the client's portal now.`);
        }
      } else {
        toast.success(`Invoice ${inv.number} saved privately. Nothing has been emailed.`);
      }
      setBuilder(null);
      await load();
      setView(inv);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const resendLink = async (inv) => {
    try {
      const u = await sendInvoiceLink(adminKey, inv.id);
      toast.success(`Pay link re-emailed to ${u.recipient_email}.`);
      await load();
      setView(u);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const acceptPayment = async (inv) => {
    const tab = window.open("", "_blank");
    try {
      const { token } = await getPayToken(adminKey, inv.id);
      const url = `${window.location.origin}/pay/${token}`;
      if (tab) tab.location.href = url;
      else window.location.assign(url);
      toast.success("Secure payment page opened — have the customer sign, then enter the card.");
    } catch (e) {
      if (tab) tab.close();
      toast.error(formatApiError(e));
    }
  };

  const convert = async (inv) => {
    try {
      const u = await convertEstimate(adminKey, inv.id);
      toast.success(`Estimate ${inv.number} converted to draft invoice ${u.number}.`);
      await load();
      setView(u);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const markStatus = async (inv, status) => {
    try {
      const u = await setInvoiceStatus(adminKey, inv.id, status);
      toast.success(status === "paid" ? `Invoice ${u.number} marked paid.` : `Invoice ${u.number} sent — the client sees it now.`);
      await load();
      setView(u);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const removeInvoice = async (inv) => {
    try {
      await deleteInvoice(adminKey, inv.id);
      toast.success(`Draft ${inv.number} deleted.`);
      setView(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (invoices === null) {
    return (
      <div className="mt-10 flex items-center gap-3 text-sm text-steel-muted">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading invoices…
      </div>
    );
  }

  return (
    <div className="mt-10" data-testid="admin-invoices">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="max-w-xl text-sm leading-relaxed text-steel-secondary">Invoices, estimates, and mobile payments.</p>
        <div className="flex flex-wrap gap-3">
        <button data-testid="invoice-new-mobile-btn" onClick={() => openNew(true)}
          className="flex items-center gap-2 border border-steel-brass px-5 py-3 text-sm font-semibold text-steel-brass transition-colors hover:bg-steel-brass/10">
          <Smartphone className="h-4 w-4" /> Mobile invoice
        </button>
        <button
          data-testid="invoice-new-btn"
          onClick={() => openNew(false)}
          className="flex items-center gap-2 bg-steel-accent px-6 py-3 text-sm font-semibold text-white shadow-[0_0_20px_rgba(168,85,247,0.30)] transition-colors duration-300 hover:bg-steel-accentHover"
        >
          <Plus className="h-4 w-4" /> New invoice
        </button>
        </div>
      </div>

      {builder && (
        <div ref={builderRef} className="mt-8 min-w-0 scroll-mt-28 border-t border-steel-border py-6 sm:py-8" data-testid="invoice-builder">
          <div className="flex items-center justify-between gap-4">
            <h3 className="font-display text-lg font-medium text-steel-text">
              {builder.kind === "mobile" ? "Mobile invoice" : builder.id ? "Edit invoice" : "New invoice"}
            </h3>
            <button
              data-testid="invoice-builder-close"
              onClick={() => setBuilder(null)}
              className="flex h-8 w-8 items-center justify-center border border-steel-border text-steel-muted transition-colors hover:text-steel-text"
              aria-label="Close builder"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          {builder.kind === "mobile" && <p data-testid="mobile-invoice-privacy" className="mt-4 text-sm text-steel-brass">Admin only · No client account · Receipt emailed after payment</p>}

          <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-2">
            <div className="min-w-0">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-steel-muted">{builder.kind === "mobile" ? "Receipt recipient" : "Billed to"}</p>
              {builder.kind !== "mobile" && <div className="mt-3 flex border border-steel-border" data-testid="invoice-recipient-modes">
                {[
                  ["client", "Portal client"],
                  ["manual", "Enter manually"],
                ].map(([id, label]) => (
                  <button
                    key={id}
                    data-testid={`invoice-mode-${id}`}
                    onClick={() => setBuilder((b) => ({ ...b, mode: id }))}
                    className={`flex-1 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                      builder.mode === id ? "bg-steel-accent text-white" : "text-steel-muted hover:text-steel-text"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>}
              {builder.mode === "client" ? (
                <select
                  data-testid="invoice-client-select"
                  value={builder.client_user_id}
                  onChange={(e) => pickClient(e.target.value)}
                  className={`${inputCls} mt-3`}
                >
                  <option value="">Select a client…</option>
                  {clients.map((c) => (
                    <option key={c.user_id} value={c.user_id}>
                      {c.name || c.email} — {c.email}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="mt-3 space-y-3">
                  <input
                    data-testid="invoice-recipient-name"
                    aria-label="Recipient name"
                    autoComplete="off"
                    maxLength={120}
                    value={builder.recipient_name}
                    onChange={(e) => setBuilder((b) => ({ ...b, recipient_name: e.target.value }))}
                    className={inputCls}
                    placeholder="Recipient name"
                  />
                  <input
                    data-testid="invoice-recipient-email"
                    aria-label="Receipt email"
                    autoComplete="off"
                    type="email"
                    value={builder.recipient_email}
                    onChange={(e) => setBuilder((b) => ({ ...b, recipient_email: e.target.value }))}
                    className={inputCls}
                    placeholder="recipient@email.com"
                  />
                </div>
              )}
              {builder.mode === "client" && builder.client_user_id && (
                <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-steel-muted" data-testid="invoice-recipient-preview">
                  {builder.recipient_name} · {builder.recipient_email}
                </p>
              )}
              <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.25em] text-steel-muted">
                Notes (optional — shown on the invoice)
              </p>
              <textarea
                data-testid="invoice-notes"
                value={builder.notes}
                onChange={(e) => setBuilder((b) => ({ ...b, notes: e.target.value }))}
                rows={3}
                className={`${inputCls} mt-3 resize-none`}
                placeholder="e.g. 2025 federal + state preparation — due on receipt"
              />
            </div>

            <div className="min-w-0">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-steel-muted">
                Forms needed — set the price per form
              </p>
              <div className="mt-3 flex gap-2">
                <select
                  data-testid="invoice-form-select"
                  value={formPick}
                  onChange={(e) => setFormPick(e.target.value)}
                  className={inputCls}
                >
                  <option value="">Pick a form from your fee schedule…</option>
                  <option value="base">Base return preparation</option>
                  {COMPLEXITY_CATEGORIES.map((cat) => {
                    const catOf = (id) => COMPLEXITY_ITEMS.find((i) => i.id === id)?.cat || "other";
                    const opts = (fees?.items || []).filter((f) => catOf(f.id) === cat.id);
                    if (opts.length === 0) return null;
                    return (
                      <optgroup key={cat.id} label={cat.label}>
                        {opts.map((f) => (
                          <option key={f.id} value={f.id}>
                            {labelForForm(f.id)} (${Number(f.min).toLocaleString()}–${Number(f.max).toLocaleString()})
                          </option>
                        ))}
                      </optgroup>
                    );
                  })}
                </select>
                <button
                  data-testid="invoice-add-form-btn"
                  onClick={addFormLine}
                  disabled={!formPick}
                  className="flex shrink-0 items-center gap-2 border border-steel-border px-4 text-xs font-medium text-steel-secondary transition-colors hover:border-steel-brass hover:text-steel-brass disabled:opacity-40"
                >
                  <Plus className="h-3.5 w-3.5" /> Add
                </button>
              </div>
              {formPick === "multi-state" && (
                <select
                  data-testid="invoice-state-select"
                  value={lineState}
                  onChange={(e) => setLineState(e.target.value)}
                  className={`${inputCls} mt-2`}
                >
                  <option value="">Which state is this return for?</option>
                  {BILLABLE_STATES.map((s) => (
                    <option key={s.code} value={s.code}>
                      {s.name} ({s.code})
                    </option>
                  ))}
                </select>
              )}

              <div className="mt-4 space-y-2" data-testid="invoice-lines">
                {builder.lines.length === 0 ? (
                  <p className="border border-dashed border-steel-border p-5 text-xs text-steel-muted">
                    No lines yet — pick forms above, or add a custom line.
                  </p>
                ) : (
                  builder.lines.map((l, i) => (
                    <div key={i} className="flex flex-wrap items-center gap-2 border border-steel-border bg-steel-elevated p-2" data-testid={`invoice-line-${i}`}>
                      <input
                        data-testid={`invoice-line-label-${i}`}
                        value={l.label}
                        onChange={(e) => setLine(i, "label", e.target.value)}
                        className="min-w-0 basis-full sm:basis-auto sm:flex-1 border-0 bg-transparent px-2 py-2 text-base sm:text-sm text-steel-text focus:outline-none"
                        placeholder="Description"
                      />
                      <div className="flex shrink-0 items-center gap-1">
                        <span className="text-xs text-steel-muted">$</span>
                        <input
                          data-testid={`invoice-line-price-${i}`}
                          type="number"
                          min="0"
                          step="0.01"
                          value={l.price}
                          onChange={(e) => setLine(i, "price", e.target.value)}
                          className="w-24 border border-steel-border bg-steel-surface px-2 py-2 text-right font-mono text-sm text-steel-text focus:border-steel-accent focus:outline-none"
                        />
                      </div>
                      <button
                        data-testid={`invoice-line-remove-${i}`}
                        onClick={() => setBuilder((b) => ({ ...b, lines: b.lines.filter((_, idx) => idx !== i) }))}
                        className="flex h-8 w-8 shrink-0 items-center justify-center text-steel-muted transition-colors hover:text-red-400"
                        aria-label="Remove line"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))
                )}
              </div>
              <button
                data-testid="invoice-add-custom-btn"
                onClick={() => setBuilder((b) => ({ ...b, lines: [...b.lines, { form_id: "", label: "", price: "" }] }))}
                className="mt-3 text-xs font-medium text-steel-muted underline-offset-4 transition-colors hover:text-steel-brass hover:underline"
              >
                + Add a custom line
              </button>

              <div className="mt-5 flex items-baseline justify-between border-t border-steel-border pt-4">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-steel-muted">Invoice total</span>
                <span data-testid="invoice-builder-total" className="neon-text font-display text-2xl font-medium text-steel-brass">
                  {money(total)}
                </span>
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  data-testid="invoice-save-draft-btn"
                  onClick={() => save(false)}
                  disabled={saving}
                  className="flex items-center gap-2 border border-steel-border px-6 py-3 text-sm font-medium text-steel-secondary transition-colors hover:border-steel-muted hover:text-steel-text disabled:opacity-50"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                  Save draft
                </button>
                {builder.kind !== "mobile" && <button
                  data-testid="invoice-save-send-btn"
                  onClick={() => save(true)}
                  disabled={saving}
                  className="flex items-center gap-2 bg-steel-accent px-6 py-3 text-sm font-semibold text-white shadow-[0_0_20px_rgba(168,85,247,0.30)] transition-colors duration-300 hover:bg-steel-accentHover disabled:opacity-50"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (builder.mode === "manual" ? <Mail className="h-4 w-4" /> : <Send className="h-4 w-4" />)}
                  {builder.mode === "manual" ? "Save & email pay link" : "Save & send"}
                </button>}
                {builder.kind === "mobile" && <button
                  data-testid="invoice-mobile-save-email-btn"
                  onClick={() => save(true)}
                  disabled={saving}
                  className="flex items-center gap-2 bg-steel-accent px-6 py-3 text-sm font-semibold text-white shadow-[0_0_20px_rgba(168,85,247,0.30)] transition-colors duration-300 hover:bg-steel-accentHover disabled:opacity-50"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                  Save & email pay link
                </button>}
              </div>
            </div>
          </div>
        </div>
      )}

      {view && !builder && (
        <div ref={detailRef} className="mt-8 scroll-mt-28 grid grid-cols-1 gap-6 lg:grid-cols-12" data-testid="invoice-detail">
          <div className="min-w-0 lg:col-span-8">
            <InvoiceSheet invoice={view} />
          </div>
          <div className="min-w-0 lg:col-span-4">
            {view.kind === "mobile" && <MobileInvoicePayment key={view.id} invoice={view} adminKey={adminKey}
              onChange={(next) => { setView(next); setInvoices((list) => list.map((inv) => inv.id === next.id ? next : inv)); }} />}
            <div className="space-y-3 border border-steel-border bg-steel-surface p-6">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-steel-muted">Actions</p>
              <button
                data-testid="invoice-print-btn"
                onClick={() => window.print()}
                className="flex w-full items-center gap-2 border border-steel-border px-4 py-3 text-sm text-steel-secondary transition-colors hover:border-steel-brass hover:text-steel-brass"
              >
                <Printer className="h-4 w-4" /> Print / Save as PDF
              </button>
              {view.kind !== "mobile" && view.status !== "paid" && view.status !== "estimate" && (
                <button
                  data-testid="invoice-accept-payment-btn"
                  onClick={() => acceptPayment(view)}
                  className="flex w-full items-center gap-2 bg-emerald-600 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-emerald-500"
                >
                  <CreditCard className="h-4 w-4" /> Accept payment (card)
                </button>
              )}
              {!view.client_user_id && view.status !== "paid" && view.status !== "estimate" && (
                <>
                  <button
                    data-testid="invoice-send-link-btn"
                    onClick={() => resendLink(view)}
                    className="flex w-full items-center gap-2 bg-steel-accent px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-steel-accentHover"
                  >
                    <Mail className="h-4 w-4" /> {view.pay_link_sent_at ? "Resend invoice by email" : "Send invoice by email"}
                  </button>
                  {view.pay_link_sent_at && (
                    <p data-testid="invoice-link-status" className="break-words text-xs text-steel-muted [overflow-wrap:anywhere]">
                      Pay link emailed to {view.recipient_email} on {new Date(view.pay_link_sent_at).toLocaleString("en-US")}. The customer can sign and pay online.
                    </p>
                  )}
                </>
              )}
              {view.status === "estimate" && (
                <button
                  data-testid="invoice-convert-btn"
                  onClick={() => convert(view)}
                  className="flex w-full items-center gap-2 bg-steel-accent px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-steel-accentHover"
                >
                  <FileSignature className="h-4 w-4" /> Convert to invoice
                </button>
              )}
              {view.status === "draft" && view.kind !== "mobile" && (
                <button
                  data-testid="invoice-mark-sent-btn"
                  onClick={() => markStatus(view, "sent")}
                  className="flex w-full items-center gap-2 bg-steel-accent px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-steel-accentHover"
                >
                  <Send className="h-4 w-4" /> Mark sent{view.client_user_id ? " (shows in portal)" : ""}
                </button>
              )}
              {view.status === "sent" && view.kind !== "mobile" && (
                <button
                  data-testid="invoice-mark-paid-btn"
                  onClick={() => markStatus(view, "paid")}
                  className="flex w-full items-center gap-2 border border-emerald-500/50 px-4 py-3 text-sm font-medium text-emerald-400 transition-colors hover:bg-emerald-500/10"
                >
                  <CheckCircle2 className="h-4 w-4" /> Mark paid
                </button>
              )}
              {view.status !== "paid" && view.status !== "estimate" && !(view.kind === "mobile" && view.signed_at) && (
                <button
                  data-testid="invoice-edit-btn"
                  onClick={() => openEdit(view)}
                  className="flex w-full items-center gap-2 border border-steel-border px-4 py-3 text-sm text-steel-secondary transition-colors hover:text-steel-text"
                >
                  <Pencil className="h-4 w-4" /> Edit invoice
                </button>
              )}
              {(view.status === "draft" || view.status === "estimate") && !(view.kind === "mobile" && view.signed_at) && (
                <button
                  data-testid="invoice-delete-btn"
                  onClick={() => removeInvoice(view)}
                  className="flex w-full items-center gap-2 border border-steel-border px-4 py-3 text-sm text-steel-muted transition-colors hover:border-red-400 hover:text-red-400"
                >
                  <Trash2 className="h-4 w-4" /> Delete {view.status === "estimate" ? "estimate" : "draft"}
                </button>
              )}
              <button
                data-testid="invoice-detail-close"
                onClick={() => setView(null)}
                className="w-full text-center text-xs text-steel-muted transition-colors hover:text-steel-text"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mt-8" data-testid="invoice-list">
        {invoices.length === 0 ? (
          <div className="flex flex-col items-start gap-4 border border-steel-border bg-steel-surface p-10">
            <FileText className="h-6 w-6 text-steel-muted" />
            <p className="text-sm text-steel-secondary">No invoices yet. Create the first one.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {invoices.map((inv) => (
              <button
                key={inv.id}
                type="button"
                data-testid={`invoice-row-${inv.id}`}
                onClick={() => { setBuilder(null); setView(inv); }}
                className={`flex w-full flex-wrap items-center justify-between gap-3 border bg-steel-surface px-5 py-4 text-left transition-colors duration-300 ${
                  view?.id === inv.id ? "border-steel-brass/60" : "border-steel-border hover:border-steel-muted"
                }`}
              >
                <div className="flex min-w-0 flex-wrap items-center gap-3">
                  <span className="font-mono text-sm font-semibold text-steel-text">{inv.number}</span>
                  <span className={`border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.2em] ${STATUS_BADGE[inv.status]}`}>
                    {inv.status === "draft" && inv.kind === "mobile" ? "Unpaid" : inv.status}
                  </span>
                  {inv.kind === "mobile" && <span data-testid={`invoice-mobile-badge-${inv.id}`} className="flex items-center gap-1 text-xs text-steel-brass"><Smartphone className="h-3.5 w-3.5" /> Mobile</span>}
                </div>
                <div className="flex min-w-0 max-w-full flex-wrap items-center gap-3 sm:gap-6">
                  <span className="min-w-0 break-words text-sm text-steel-secondary [overflow-wrap:anywhere]">
                    {inv.recipient_name} <span className="text-steel-muted">· {inv.recipient_email}</span>
                  </span>
                  <span className="font-mono text-sm text-steel-brass">
                    {inv.status === "estimate"
                      ? `$${Number(inv.estimate_min || 0).toLocaleString()} – $${Number(inv.estimate_max || inv.total).toLocaleString()}`
                      : money(inv.total)}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
